import { Button } from '@mui/material'
import {
  resumeAudio,
  startCallTelemetry,
  type CallPrompt,
  type CallTelemetry,
  type TelemetryLog,
  type ZoomClient,
} from '@fireflyhealth/zoom-web'
import { useSnackbar } from 'notistack'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { logger } from '~/shared/lib/logger'

const ENABLE_AUDIO_KEY = 'zoom-enable-audio'
const REFRESH_PAGE_KEY = 'zoom-refresh-page'
const MUTED_KEY = 'zoom-speaking-while-muted'

const logTelemetry: TelemetryLog = (level, message, details) => logger[level](message, details)

/**
 * Starts and stops the visit's call telemetry, and turns its prompts into notices. `start` is
 * called as the join begins, so the telemetry can time it.
 */
export const useCallTelemetry = (client: ZoomClient, appointmentId: number) => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar()
  const telemetry = useRef<CallTelemetry | null>(null)
  const isUnmounted = useRef(false)

  const promptAction = useCallback(
    (key: string, label: string, onClick: () => void) => (
      <Button
        color="inherit"
        size="small"
        onClick={() => {
          closeSnackbar(key)
          onClick()
        }}
      >
        {label}
      </Button>
    ),
    [closeSnackbar],
  )

  const showPrompt = useCallback(
    (prompt: CallPrompt) => {
      if (prompt === 'enable-audio') {
        enqueueSnackbar('Your browser paused the call audio.', {
          key: ENABLE_AUDIO_KEY,
          variant: 'warning',
          persist: true,
          preventDuplicate: true,
          action: promptAction(ENABLE_AUDIO_KEY, 'Enable audio', () => {
            resumeAudio(client).catch(e => logger.error('[Zoom] resumeAudio', e))
          }),
        })
        return
      }
      if (prompt === 'refresh-page') {
        enqueueSnackbar('There is a problem with your camera or microphone.', {
          key: REFRESH_PAGE_KEY,
          variant: 'error',
          persist: true,
          preventDuplicate: true,
          action: promptAction(REFRESH_PAGE_KEY, 'Refresh page', () => window.location.reload()),
        })
        return
      }
      enqueueSnackbar("You're muted. Unmute to be heard.", { key: MUTED_KEY, variant: 'info' })
    },
    [promptAction, client, enqueueSnackbar],
  )

  const stop = useCallback(() => {
    telemetry.current?.stop()
    telemetry.current = null
    closeSnackbar(ENABLE_AUDIO_KEY)
    closeSnackbar(REFRESH_PAGE_KEY)
  }, [closeSnackbar])

  const start = useCallback(() => {
    stop()
    const instance = startCallTelemetry(client, {
      appointmentId,
      log: logTelemetry,
      // On the logger's global context, so every line logged during the visit carries the Zoom ids.
      onContext: context => logger.setGlobalContext('zoom', context ?? undefined),
      onPrompt: showPrompt,
    })
    // A join can resume after unmount, once a previous leave settles; nothing would stop it then.
    if (isUnmounted.current) instance.stop()
    else telemetry.current = instance
    return instance
  }, [appointmentId, client, showPrompt, stop])

  useEffect(() => {
    isUnmounted.current = false
    return () => {
      isUnmounted.current = true
      stop()
    }
  }, [stop])

  return useMemo(() => ({ start, stop }), [start, stop])
}
